/**
 * A container for overlays like modals and popovers. Renders the overlay
 * into a Portal which is placed at the end of the document body.
 * Also ensures that the overlay is hidden from screen readers if a
 * nested modal is opened. Only the top-most modal or overlay should
 * be accessible at once.
 */
import { useEffect, ReactPortal } from 'react';
import { createPortal } from 'react-dom';

import type { ModalContextProviderProps } from '../providers/ModalContextProvider.js';
import { OverlayProvider } from '../providers/OverlayProvider.js';

/**
 * @public
 */
export interface OverlayContainerProps extends ModalContextProviderProps {
  /**
   * The container element in which the overlay portal will be placed.
   * @defaultValue document.body
   */
  portalContainer?: HTMLElement;
}

/**
 * Use the `OverlayContainer` to create a portal, rendering children in a container
 * that is located outside the hierarchy of the parent component.
 * @public
 */
export function OverlayContainer(props: OverlayContainerProps): ReactPortal {
  const { portalContainer = document.body, ...remainingProps } = props;

  useEffect(() => {
    if (portalContainer.closest('[data-overlay-container]')) {
      throw new Error(
        'An OverlayContainer must not be inside another container. Please change the portalContainer prop.',
      );
    }
  }, [portalContainer]);

  const contents = <OverlayProvider {...remainingProps} />;
  return createPortal(contents, portalContainer);
}
